import React, {
    type ChangeEventHandler, useCallback,
    useState,
} from 'react';

import debounce from 'lodash.debounce';
import {searchResources} from 'src/openapi/admin/apis/EntitlementsResourceSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';


import {AddResourceSuggestResult} from './add-resource-suggest-result';
import {
    ERROR_MESSAGE,
    MAX_RESULTS,
} from './constants';


import type {ResourceSearchItem} from 'src/openapi/admin/models/ResourceSearchItem';
import type {ServerResponse} from 'src/typedef';

type Props = {
    id: string,
    selectedResources: string[],
    onSearch: (resourceId: string) => void,
};

export const AddResourceSuggest: React.FC<Props> = ({id, onSearch, selectedResources}) => {
    const apiDispatch = useAppDispatch();
    const {createAbortSignal, abort} = useAbortSignal();

    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState<ServerResponse<ResourceSearchItem[]> | null>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getSuggestions = useCallback(debounce(async (newValue: string) => {
        const signal = createAbortSignal();
        const resourceName = newValue.toLowerCase().trim();

        if (resourceName && resourceName.length > 1) {
            const searchResult = await apiDispatch(searchResources({resourceName, includeDatasets: true}, {signal}));
            if (searchResult.payload.success) {
                setResponse({data: searchResult.payload.data.slice(0, MAX_RESULTS)});
            } else {
                setResponse({error: {message: ERROR_MESSAGE}});
            }
            setBusy(false);
        } else {
            setBusy(false);
            setResponse(null);
            abort();
        }
    }, 1000), [apiDispatch, createAbortSignal, abort]);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setBusy(true);
        setQuery(value);
        getSuggestions(value);
    }, [getSuggestions]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        setResponse(null);
    }, []);

    const onItemSelect = useCallback((grantId: string) => {
        onSearch(grantId);
        setResponse(null);
        setQuery('');
    }, [onSearch]);

    return (
        <div style={{width: 500}}>
            <Autosuggest
                id={id}
                value={query}
                onChange={onChangeQuery}
                placeholder="Search for resources by keyword"
                onItemSelect={onItemSelect}
                onClear={clearSuggest}>
                <AddResourceSuggestResult
                    id={id}
                    busy={busy}
                    query={query}
                    response={response}
                    selectedResources={selectedResources}
                />
            </Autosuggest>
        </div>
    );
};
